import { useCallback, useMemo, useRef } from 'react';
import { employeeApi, useAppDispatch, useSelectedBrandKey } from '@dap/common/data-access';
import { EmployeeSearchResult } from '@dap/memberadmin/types';
import { useGetCommonSocialFeedBrandsQuery } from '@dap/sanity/data-access';
import { DocumentType, SocialFeed } from '@dap/sanity/types';
import { useGetSocialFeedsQuery } from './socialFeedApi';

export type MentionCandidate = Pick<EmployeeSearchResult, 'userId' | 'name'> & {
  jobTitle?: string | null;
};

export const MENTION_MIN_QUERY_LENGTH = 2;

/**
 * - open: anyone in `brandKeys` may be mentioned (unrestricted feed, or no feed given).
 *   For a common feed that's every brand the feed is shown in, otherwise just the current brand.
 * - restricted: only the users returned by `loadAllowedUsers` may be mentioned
 * - pending: the feed's access list isn't known yet (still loading, or failed to load).
 *   Treated as "suggest nobody" so a restricted feed never falls back to brand-wide results.
 */
export type FeedMentionScope =
  | { status: 'open'; brandKeys: string[] }
  | { status: 'pending' }
  | { status: 'restricted'; loadAllowedUsers: () => Promise<MentionCandidate[]> };

export function filterMentionCandidates(
  candidates: MentionCandidate[],
  query: string
): MentionCandidate[] {
  const needle = query.trim().toLowerCase();
  if (needle.length < MENTION_MIN_QUERY_LENGTH) {
    return [];
  }
  return candidates.filter((candidate) => candidate.name?.toLowerCase().includes(needle));
}

const NO_USER_IDS: string[] = [];

/**
 * Works out who may be @-mentioned in a social feed. A brand feed limited to certain
 * employees (`userIds`) only offers those; the list is read from the feeds query, and the
 * names are looked up lazily on first use since the list can be long. A common feed offers
 * employees from every brand it's shown in.
 */
export function useFeedMentionScope(socialFeedId?: string): FeedMentionScope {
  const brandKey = useSelectedBrandKey();
  const dispatch = useAppDispatch();
  const { data: feeds } = useGetSocialFeedsQuery({ brandKey }, { skip: !socialFeedId });

  const feed = feeds?.find((candidate) => candidate._id === socialFeedId);
  const isCommonFeed = feed?._type === DocumentType.CommonSocialFeed;
  const { data: commonFeedBrandKeys } = useGetCommonSocialFeedBrandsQuery(
    { feedId: feed?._id ?? '' },
    { skip: !isCommonFeed }
  );
  // Always includes the current brand, and falls back to just that while the brands load.
  // Sorted and keyed by string so the array stays stable across renders.
  const brandKeysKey = [...new Set([brandKey, ...((isCommonFeed && commonFeedBrandKeys) || [])])]
    .sort()
    .join(',');
  const brandKeys = useMemo(() => brandKeysKey.split(','), [brandKeysKey]);
  // Only brand feeds carry `userIds`; common feeds are open to everyone
  const feedUserIds = (feed as SocialFeed | undefined)?.userIds;
  const userIds = feedUserIds?.length ? feedUserIds : NO_USER_IDS;
  const userIdsKey = userIds.join(',');

  const cache = useRef<{ key: string; promise: Promise<MentionCandidate[]> } | null>(null);
  const loadAllowedUsers = useCallback(() => {
    if (cache.current?.key === userIdsKey) {
      return cache.current.promise;
    }
    const promise = Promise.all(
      userIds.map((userId) =>
        dispatch(employeeApi.endpoints.getEmployee.initiate({ userId }, { subscribe: false }))
          .unwrap()
          .then(
            (employee): MentionCandidate => ({
              userId,
              name: employee.name,
              jobTitle: employee.jobTitle,
            })
          )
          .catch(() => null)
      )
    ).then((resolved) => resolved.filter((candidate) => candidate !== null));
    cache.current = { key: userIdsKey, promise };
    return promise;
    // userIds is derived from userIdsKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdsKey, dispatch]);

  return useMemo<FeedMentionScope>(() => {
    if (!socialFeedId) {
      return { status: 'open', brandKeys };
    }
    if (!feeds) {
      return { status: 'pending' };
    }
    if (userIds.length === 0) {
      return { status: 'open', brandKeys };
    }
    return { status: 'restricted', loadAllowedUsers };
  }, [socialFeedId, feeds, userIds.length, loadAllowedUsers, brandKeys]);
}
